import type { Viewer } from '@easterngraphics/wcf/modules/core/view';
import type { AsyncFunction } from '@egr/xbox/utils/Types';

import 'pepjs'; // pointerEvent polyfill
import '@easterngraphics/wcf/styles/cf.css';
import '@easterngraphics/wcf/styles/core.css';
import '@easterngraphics/wcf/styles/pl.css';

import { EventManager } from './managers/EventManager';

import { CreationEvent } from '@egr/xbox/utils/Core';
import { Logger } from '@egr/xbox/utils/errors/Logger';

import { ListenableEvent } from '@easterngraphics/wcf/modules/utils/ListenableEvent';

export enum EventReason {
    /**
     * For some language the default encoding must be changed. Right now, a new
     * EAIWS session is the only way to ensure the correct encoding.
     */
    DEFAULT_ENCODING_CHANGED = 1 << 0,
    PCON_LOGIN_STATE_CHANGED = 1 << 1,
}

export interface SessionTaskQueue<Arguments extends Array<unknown> = []> {
    addTask: (debugLabel: string, callback: AsyncFunction<void, Arguments>) => void;
}

interface PreInitializationTasks {
    postConnect: SessionTaskQueue;
}

/**
 * Note: it is not guaranteed that this event will ever be triggered
 */
export interface PreInitializationEvent {
    type: 'pre-initialization';
    tasks: PreInitializationTasks;
    primaryDataLanguage?: string;
}

export interface OpenSessionEventOptions {
    primaryDataLanguage?: string;
    newProject?: boolean;
    importFormat?: string;
    eventReason?: EventReason;
}

export interface OpenSessionTasks {
    /**
     * @deprecated Note: newer EAIWS versions support the clearing of an already
     * established session. Therefore, in most cases, the session will already
     * have been created before this event is triggered.
     */
    preConnect: SessionTaskQueue<[OpenSessionEventOptions]>;
    /**
     * In this step the session data should be loaded (e.g. OBK import)
     */
    loading: SessionTaskQueue<[OpenSessionEventOptions]>;
    /**
     * This step can be used to load settings from the session itself.
     * (e.g. data stored in the OBK file)
     */
    postLoading: SessionTaskQueue<[OpenSessionEventOptions]>;
    preProcessing: SessionTaskQueue<[OpenSessionEventOptions]>;
    processing: SessionTaskQueue<[OpenSessionEventOptions]>;
    postProcessing: SessionTaskQueue<[OpenSessionEventOptions]>;
}

export interface OpenSessionEvent {
    type: 'open';
    tasks: OpenSessionTasks;
    options: OpenSessionEventOptions;
}

export interface CloseSessionTasks {
    preCleaning: SessionTaskQueue<[CloseSessionEventOptions]>;
    cleaning: SessionTaskQueue<[CloseSessionEventOptions]>;
    postClose: SessionTaskQueue<[CloseSessionEventOptions]>;
}

export interface CloseSessionEventOptions {
    eventReason?: EventReason;
}

export interface CloseSessionEvent {
    type: 'close';
    tasks: CloseSessionTasks;
    options: CloseSessionEventOptions;
}

export interface SaveSessionTasks {
    updating: SessionTaskQueue;
    saving: SessionTaskQueue;
}

export interface SaveSessionEvent {
    type: 'save';
    tasks: SaveSessionTasks;
}

export type SessionEvent = OpenSessionEvent | SaveSessionEvent | CloseSessionEvent | PreInitializationEvent;

export interface AppProps {}

class SessionListableEvents {
    public readonly preInitialization = new ListenableEvent<PreInitializationEvent, SessionListableEvents>(this);
    public readonly open = new ListenableEvent<OpenSessionEvent, SessionListableEvents>(this);
    public readonly close = new ListenableEvent<CloseSessionEvent, SessionListableEvents>(this);
    public readonly save = new ListenableEvent<SaveSessionEvent, SessionListableEvents>(this);

    public trigger(event: SessionEvent): void {
        switch (event.type) {
            case 'pre-initialization':
                this.preInitialization.trigger(event);
                break;
            case 'open':
                this.open.trigger(event);
                break;
            case 'save':
                this.save.trigger(event);
                break;
            case 'close':
                this.close.trigger(event);
                break;
            default:
                Logger.error(new Error('unhandled event'), event);
                break;
        }
    }
}

interface AppEvents {
    // Note: do not use async events. The handlers should use the `Lock` instances provided
    // via the `stages` argument for async tasks
    session: SessionListableEvents;
    project: {
        changed: EventManager<{
            // If set to `true`, the project must be saved immediately.
            // Note: this may lead to a situation where user interaction is required (e.g. project was not saved before)
            saveImmediately?: boolean;
            // If set to `true`, the event should be ignored if the project was already saved before
            ignoreIfSavedBefore?: boolean;
        }>;
    };
    viewCreated: CreationEvent<Viewer, undefined>;
}

export const appEvents: AppEvents = {
    session: new SessionListableEvents(),
    project: { changed: new EventManager() },
    viewCreated: new CreationEvent<Viewer, undefined>(undefined),
};